/**
 * Conversation List Component - Displays list of agent conversations
 */

import React, { useState } from "react";
import {
  List,
  Card,
  Tag,
  Space,
  Typography,
  Button,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Empty,
  Spin,
  message,
  Avatar,
} from "antd";
import {
  MessageOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useAgent } from "../context/AgentContext.jsx";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

const ConversationList = ({ onSelectConversation }) => {
  const { conversations, stats, loading, loadConversations } = useAgent();

  const [filters, setFilters] = useState({
    status: "",
    search: "",
  });

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    loadConversations(newFilters);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";
      case "active":
        return "green";
      case "closed":
        return "red";
      default:
        return "default";
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <ClockCircleOutlined />;
      case "active":
        return <CheckCircleOutlined />;
      case "closed":
        return <CloseCircleOutlined />;
      default:
        return <MessageOutlined />;
    }
  };

  // Format conversation item
  const renderConversationItem = (conversation) => {
    const isActive = conversation.status === "active";
    const isPending = conversation.status === "pending";
    const hasUnread = conversation.unreadCount > 0;

    return (
      <List.Item
        key={conversation.id}
        onClick={() => onSelectConversation(conversation)}
        style={{
          cursor: "pointer",
          backgroundColor: hasUnread ? "#f0f8ff" : "transparent",
          border: isActive ? "2px solid #52c41a" : "1px solid #f0f0f0",
          borderRadius: "8px",
          marginBottom: "8px",
          padding: "12px",
        }}
        actions={[
          <Text key="timestamp" type="secondary" style={{ fontSize: "12px" }}>
            {dayjs(conversation.lastMessageAt).fromNow()}
          </Text>,
        ]}
      >
        <List.Item.Meta
          avatar={
            <Avatar
              icon={<UserOutlined />}
              style={{
                backgroundColor: hasUnread ? "#1890ff" : "#d9d9d9",
              }}
            />
          }
          title={
            <Space>
              <Text strong>{conversation.customerPhone}</Text>
              {hasUnread && (
                <Tag color="blue" style={{ margin: 0 }}>
                  {conversation.unreadCount} new
                </Tag>
              )}
            </Space>
          }
          description={
            <Space direction="vertical" size={0} style={{ width: "100%" }}>
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {conversation.latestMessage?.content || "No messages yet"}
              </Text>
              <Space size="small">
                <Tag
                  color={getStatusColor(conversation.status)}
                  icon={getStatusIcon(conversation.status)}
                >
                  {conversation.status}
                </Tag>
                {conversation.agentId && (
                  <Tag color="purple">Agent: {conversation.agentId}</Tag>
                )}
                {conversation.source && (
                  <Tag color="cyan">{conversation.source}</Tag>
                )}
              </Space>
            </Space>
          }
        />
      </List.Item>
    );
  };

  return (
    <Card title="Agent Conversations" style={{ height: "100%" }}>
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic
            title="Pending"
            value={stats.pending}
            valueStyle={{ color: "#fa8c16" }}
            prefix={<ClockCircleOutlined />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Active"
            value={stats.active}
            valueStyle={{ color: "#52c41a" }}
            prefix={<CheckCircleOutlined />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Closed"
            value={stats.closed}
            valueStyle={{ color: "#ff4d4f" }}
            prefix={<CloseCircleOutlined />}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Total"
            value={stats.total}
            valueStyle={{ color: "#1890ff" }}
            prefix={<MessageOutlined />}
          />
        </Col>
      </Row>

      {/* Filters */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Select
            placeholder="Filter by status"
            value={filters.status}
            onChange={(value) => handleFilterChange("status", value)}
            style={{ width: "100%" }}
            allowClear
          >
            <Option value="pending">Pending</Option>
            <Option value="active">Active</Option>
            <Option value="closed">Closed</Option>
          </Select>
        </Col>
        <Col span={12}>
          <Search
            placeholder="Search by phone number"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            onSearch={(value) => handleFilterChange("search", value)}
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      {/* Refresh button */}
      <Button
        type="primary"
        icon={<FilterOutlined />}
        onClick={() => loadConversations(filters)}
        style={{ marginBottom: 16 }}
      >
        Refresh
      </Button>

      {/* Conversation list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Spin size="large" />
        </div>
      ) : conversations.length > 0 ? (
        <List
          dataSource={conversations}
          renderItem={renderConversationItem}
          style={{ maxHeight: "600px", overflowY: "auto" }}
        />
      ) : (
        <Empty
          description="No conversations found"
          style={{ padding: "40px" }}
        />
      )}
    </Card>
  );
};

export default ConversationList;
